'use client';

import { Link } from '@packages/modules/src/Link/Link';
import { useTrackMarketing } from '@packages/shared/src/hooks/useTrackMarketing/useTrackMarketing';
import { type ReactNode, type Ref, useRef } from 'react';
import { Tooltip } from '@packages/modules/src/Tooltip/Tooltip';
import { ConditionalWrapper } from '@packages/shared/src/ui/ConditionalWrapper/ConditionalWrapper';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { useMediaType } from '@packages/shared/src/hooks/useMediaType/useMediaType';
import Image from 'next/image';
import { buildLinkWithArguments } from '../../utils/buildLinkWithArguments';
import { useAssignLinkTargetBasedOnBotStatus } from '../../hooks/useAssignLinkTargetBasedOnBotStatus';
import type { CmsImageType } from '../../components/CmsImage/types';
import { type Positioning } from '../../../interfaces';
import { useGetTooltipContent } from '../../hooks/useGetTooltipContent';

export type BannerItemType = {
  data: CmsImageType;
};

const LinkWrapper = ({
  children,
  data,
}: {
  children: ReactNode;
} & BannerItemType) => {
  const {
    field_link: link,
    field_link_target: linkTarget,
    field_tracking: linkTracking,
    field_link_anchor: linkAnchor,
    field_link_highlight: linkHighlight,
    field_link_searchfilter: linkSearchFilter,
    field_bonus_number: bonusNumber,
    behavior_paragraph_marketing_display_type: marketingType,
    behavior_paragraph_campaign_type: campaignType,
    behavior_paragraph_campaignid: campaignId,
    behavior_paragraph_category: category,
    behavior_paragraph_detail: detail,
    behavior_paragraph_name: name,
  } = data;

  const tmpLink = buildLinkWithArguments(link, {
    field_tracking: linkTracking || undefined,
    field_link_anchor: linkAnchor || undefined,
    field_link_highlight: linkHighlight || undefined,
    field_link_searchfilter: linkSearchFilter || undefined,
    field_bonus_number: bonusNumber || undefined,
  });

  const assignLinkTarget = useAssignLinkTargetBasedOnBotStatus();
  const finalLink = assignLinkTarget(tmpLink);

  const ref = useRef<HTMLDivElement | HTMLAnchorElement>(null);

  const isAd = marketingType === 'ad';
  const isCampaign = !isAd; // for backwards compatibility, every marketing that's not an ad is a campaign

  const { trackClick } = useTrackMarketing(
    {
      linkReady: finalLink,
      ...(campaignType && {
        ...(isAd && {
          glycerinAdId: campaignId ?? undefined, // yes, the campaignid from drupal is used as adId
          glycerinAdType: campaignType ?? undefined, // yes, the campaigntype from drupal is used as adType
        }),
        ...(isCampaign && {
          glycerinCampaignId: campaignId ?? undefined,
          glycerinCampaignType: campaignType ?? undefined,
        }),

        glycerinCategory: category ?? undefined,
        glycerinDetail: detail ?? undefined,
        glycerinName: name ?? undefined,
      }),
    },
    ref,
  );

  return (
    <Link
      ref={ref as Ref<HTMLAnchorElement>}
      href={finalLink}
      openInNewTab={!!(linkTarget && linkTarget === '_blank')}
      onClick={trackClick}
    >
      {children}
    </Link>
  );
};

const TooltipWrapper =
  (tooltipContent?: string, tooltipPositioning?: Positioning) => (ch: ReactNode) => (
    <Tooltip content={tooltipContent || undefined} positioning={tooltipPositioning}>
      {ch}
    </Tooltip>
  );

/**
 * Component that displays a banner image.
 * If a link is provided in the data, the image acts as a clickable link.
 *
 * @component
 * @param {Object} props - Properties passed to the component.
 * @param {CmsImageType} props.data - Data object containing banner details.
 * @returns {JSX.Element} A rendered banner item.
 *
 * @example
 * // Usage example:
 * <BannerItem data={someCmsImageData} />
 */
export const BannerItem = ({ data }: BannerItemType) => {
  const {
    field_link: link,
    field_alternate_text: imageAlt,
    field_image_default: imageDefault,
    field_image_mobile: imageMobile,
    promotion_item: tooltipDataPromotion,
    field_content_snippet_tooltip: tooltipDataContentSnippet,
    behavior_paragraph_tooltip_activation: tooltipActivation,
    behavior_paragraph_tooltip_positioning: tooltipPositioning,
  } = data;
  const isDesktop = useMediaType();
  const {
    assets: {
      cmsImage: {
        imageOptimizationEnabled,
        uriTransformSource,
        uriTransformEndpoint,
        imageQuality,
      },
    },
  } = useConfig();

  const tooltipContent = useGetTooltipContent(tooltipDataPromotion, tooltipDataContentSnippet);

  const imageData = [!isDesktop && imageMobile, imageDefault].find(
    (image) => image && typeof image.uri === 'object' && image.uri?.url,
  );

  if (!imageData) {
    return null;
  }

  const { width: imageDataWidth, height: imageDataHeight } = imageData?.meta || {};
  const { url: imageUri = '', value: imageValue = null } =
    (typeof imageData?.uri === 'object' && imageData.uri) || {};

  return (
    <div className="mb-2 block [&_img]:block [&_img]:h-auto [&_img]:max-w-full">
      <ConditionalWrapper
        condition={!!(tooltipContent && !!tooltipActivation)}
        wrapper={TooltipWrapper(tooltipContent, tooltipPositioning)}
      >
        <ConditionalWrapper
          condition={Boolean(link)}
          wrapper={(children) => LinkWrapper({ children, data })}
        >
          {imageValue && imageDataWidth && imageDataHeight ? (
            <Image
              src={
                imageOptimizationEnabled
                  ? imageValue.replace(uriTransformSource, uriTransformEndpoint)
                  : imageUri
              }
              alt={imageAlt || ''}
              width={
                typeof imageDataWidth === 'number'
                  ? imageDataWidth
                  : Number.parseInt(imageDataWidth, 10)
              }
              height={
                typeof imageDataHeight === 'number'
                  ? imageDataHeight
                  : Number.parseInt(imageDataHeight, 10)
              }
              loading="lazy"
              {...(imageOptimizationEnabled && { quality: imageQuality })}
              {...(!imageOptimizationEnabled && { unoptimized: true })}
            />
          ) : (
            <img src={imageUri} alt={imageAlt || ''} loading="lazy" />
          )}
        </ConditionalWrapper>
      </ConditionalWrapper>
    </div>
  );
};
